import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Play, Pause, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Compact voice recorder. Calls onRecorded(Blob, mimeType, durationSec) when the user
 * confirms; or onCleared() if they discard. Visually fits the Islamic palette.
 */
export default function VoiceRecorder({ onRecorded, onCleared, maxSeconds = 90, testidPrefix = "voice" }) {
  const { t: tr } = useLanguage();
  const [state, setState] = useState("idle"); // idle | recording | preview | uploading
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
  }, [previewUrl]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        setState("preview");
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= maxSeconds) { stop(); return s + 1; }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error(tr("voiceRecorder.couldnTAccessMicrophone"));
    }
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    blobRef.current = null;
    setPreviewUrl(null);
    setSeconds(0);
    setState("idle");
    onCleared && onCleared();
  };

  const confirm = async () => {
    if (!blobRef.current) return;
    setState("uploading");
    try {
      await onRecorded(blobRef.current, blobRef.current.type, seconds);
      discard();
    } catch {
      setState("preview");
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={start}
        data-testid={`${testidPrefix}-record-btn`}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10 transition"
      >
        <Mic className="h-4 w-4" /> {tr("voiceRecorder.recordVoiceNote")}
      </button>
    );
  }
  if (state === "recording") {
    return (
      <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium bg-terracotta-500/15 text-terracotta-600 border border-terracotta-500/40" data-testid={`${testidPrefix}-recording`}>
        <span className="h-2 w-2 rounded-full bg-terracotta-500 animate-pulse" />
        <span>{tr("voiceRecorder.recording", { time: fmt(seconds) })}</span>
        <button type="button" onClick={stop} data-testid={`${testidPrefix}-stop-btn`} className="ml-1.5 p-1 rounded-full bg-terracotta-500 text-white hover:bg-terracotta-600">
          <Square className="h-3 w-3 fill-white" />
        </button>
      </div>
    );
  }
  if (state === "preview" || state === "uploading") {
    return (
      <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm border border-forest-600/30 bg-forest-600/5" data-testid={`${testidPrefix}-preview`}>
        <button type="button" onClick={togglePlay} className="p-1 rounded-full bg-forest-600 text-sand-100" data-testid={`${testidPrefix}-play-btn`}>
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </button>
        <audio
          ref={audioRef} src={previewUrl}
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        />
        <span className="text-forest-700 font-medium">{fmt(seconds)}</span>
        <button type="button" onClick={discard} disabled={state === "uploading"} className="p-1 rounded-full text-terracotta-600 hover:bg-terracotta-500/10" data-testid={`${testidPrefix}-discard-btn`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={confirm} disabled={state === "uploading"} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`${testidPrefix}-confirm-btn`}>
          {state === "uploading" ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> {tr("hifzTest.sending")}</>) : tr("voiceRecorder.send")}
        </button>
      </div>
    );
  }
}
